import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { track, trackPageview } from '@/lib/analytics'

// Route segments that are NOT an organization slug. Anything else in the first
// path position is treated as the org slug, so the module is the second
// segment. Keep this in sync with src/lib/router/routes/*.
const NON_ORG_SEGMENTS = new Set([
  'about-scrambl',
  'account',
  'auth',
  'beta',
  'benny-companion',
  'book',
  'client-portal',
  'csat',
  'delete-account',
  'desktop-settings',
  'e',
  'embed',
  'external-org-invite',
  'external-org-support-request',
  'f',
  'forgot-password',
  'help',
  'invite',
  'kb',
  'login',
  'mcp',
  'open',
  'organization-setup',
  'organizations',
  'portal',
  'q',
  'register',
  'reset-password',
  'roadmap',
  'settings',
  'share',
  'today',
  'tray-tasks',
  'unauthorized',
  'unsubscribe',
  'user-setup',
  'whats-new',
])

// The subset of non-org routes worth reporting as a module. Everything else in
// NON_ORG_SEGMENTS (auth, OAuth callbacks, public share links) is not a module
// the user "opened" and is skipped.
const GLOBAL_ROUTE_MODULES: Record<string, string> = {
  'about-scrambl': 'about',
  account: 'account',
  'client-portal': 'client_portal',
  'desktop-settings': 'settings',
  help: 'help',
  organizations: 'organizations',
  settings: 'settings',
  today: 'today',
  'tray-tasks': 'tray_tasks',
  'whats-new': 'whats_new',
}

// Org-scoped module segments. An allowlist, not a passthrough: unknown
// segments (custom pages live at /:orgSlug/:pageSlug) collapse to `other` so
// module cardinality stays bounded.
const ORG_MODULE_SEGMENTS = new Set([
  'admin',
  'analytics',
  'calendar',
  'collections',
  'communications',
  'contacts',
  'contracts',
  'deals',
  'documents',
  'entities',
  'events',
  'expenses',
  'financials',
  'focus',
  'forms',
  'getting-started',
  'help',
  'inbox',
  'inventory',
  'invoices',
  'knowledge-base',
  'leads',
  'live-chat',
  'marketing',
  'meals',
  'meetings',
  'my-notes',
  'my-organization',
  'my-tasks',
  'my-time',
  'notes',
  'notifications',
  'org-chart',
  'products',
  'projects',
  'quotes',
  'reports',
  'scheduling',
  'search',
  'settings',
  'subscription',
  'subscription-setup',
  'suppliers',
  'tasks',
  'team',
  'threads',
  'tickets',
  'time',
  'time-off',
  'timesheets',
  'today',
  'user-settings',
  'workflows',
])

/**
 * Resolve the product module a pathname belongs to. Returns null for routes
 * that aren't a module (auth, callbacks, public links).
 */
export function getModuleFromPath(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean)
  const first = segments[0]
  if (!first) return null

  if (NON_ORG_SEGMENTS.has(first)) {
    return GLOBAL_ROUTE_MODULES[first] ?? null
  }

  // /:orgSlug — the org landing route.
  const moduleSegment = segments[1]
  if (!moduleSegment) return 'home'

  return ORG_MODULE_SEGMENTS.has(moduleSegment)
    ? moduleSegment.replace(/-/g, '_')
    : 'other'
}

export function useTrackPageView(): void {
  const location = useLocation()
  // `module_opened` is deliberately coarser than a pageview: it fires when the
  // user moves BETWEEN modules, not on every route change inside one.
  const lastModuleRef = useRef<string | null>(null)

  useEffect(() => {
    trackPageview(location.pathname + location.search)
  }, [location.pathname, location.search])

  useEffect(() => {
    const module = getModuleFromPath(location.pathname)
    if (!module || module === lastModuleRef.current) return
    lastModuleRef.current = module
    track('module_opened', { module })
  }, [location.pathname])
}
